import React, { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { NotFoundPage } from '../NotFoundPage';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Switch,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Link,
  Badge,
  Slider,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Shuffle as ShuffleIcon,
  PersonAdd as InviteIcon,
  RestartAlt as ResetIcon,
  AutoFixHigh as AutofillIcon,
  Settings as SettingsIcon,
  SwapVert as SwapIcon,
  MenuBook as RulesIcon,
  History as AuditIcon,
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  Add as AddIcon,
  TouchApp as AssignIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Share as ShareIcon,
  VisibilityOff as VisibilityOffIcon,
  HelpOutline as HelpOutlineIcon,
  MergeType as MergeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useBoard, useSquares, useHits, useLedger, useAuth, useCredits, useNotifications, useShare } from '../../hooks';
import { useDocumentTitle, useDebouncedCallback } from '../../hooks/core';
import { useBoardEvents } from '../../hooks/useBoardEvents';
import { SquaresGrid, LoadingSpinner, IconPicker, BoardExport, SettingsPanel, ThemeWrapper, ConfirmDialog } from '../../components';
import { HitsPanel, HowToPlayModal, AddConnectionDialog, AssignModeDropdown, MySquaresPanel, BadNumbersWinnersSection, RulesPanel, BoardProgressGuidance, PlayerStatsBanner, LedgerPanel, SquareRequestsPanel, AuditPanel, LiveGameBanner } from '../../components/board';
import type { AssignModePlayerSelection } from '../../components/board';
import { SquareRequestBanner } from '../../components/board/SquareRequestBanner';
import { copyToClipboard } from '../../utils/clipboard';
import { PlayerAvatar } from '../../components/shared/PlayerAvatar';
import { ShareModal } from '../../components/shared/ShareModal';
import type { User, SquareRequest, BoardOfflinePlayer, HighlightedCellsWithQuarter, MergeRequest, GlobalMergeRequestDetails } from '../../types';
import { getTotalCells, isSuperAdmin, isSupport } from '../../types';
import { boardsApi, squaresApi, squareRequestsApi, ledgerApi, offlinePlayersApi, mergeRequestsApi, sportsApi, liveScoresApi } from '../../api';
import type { PendingScoreUpdate } from '../../types';
import { usePlayersStore, selectOfflinePlayers } from '../../stores';
import { sanitizeHtml } from '@katapult/ui';
import { hasSeenEducationModal, markEducationModalSeen } from '../../lib/educationModals';
import type { TeamMapping } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  'data-testid'?: string;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, 'data-testid': testId }) => (
  <div role="tabpanel" hidden={value !== index} data-testid={testId}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

export const BoardDetailPage: React.FC = () => {
  const { boardIdOrSlug, tab: tabParam } = useParams<{ boardIdOrSlug: string; tab?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshCredits } = useCredits();
  const { subscribeToGlobalMergeChanges } = useNotifications();
  const { board, isLoading, error, lockBoard, unlockBoard, randomizeNumbers, publishBoard, unpublishBoard, completeBoard, inviteParticipant, refetch, updateBoard } = useBoard(boardIdOrSlug!);

  // Handle merge suggestion from notification (query params: ?merge=offlinePlayerId&target=userId)
  const mergeSuggestionOfflinePlayerId = searchParams.get('merge');
  const mergeSuggestionTargetUserId = searchParams.get('target');

  // Set page title dynamically based on board name
  useDocumentTitle(board?.name);
  const { grid, isLoading: isSquaresLoading, claimSquare, assignSquare, clearSquare, refetch: refetchSquares } = useSquares(boardIdOrSlug!);
  const { hits, refetch: refetchHits } = useHits(boardIdOrSlug!, board?.status);
  const { summary, entries: ledgerEntries, fetchLedger } = useLedger(boardIdOrSlug!);

  // Get the URL identifier to use (prefer slug, fallback to id)
  const boardUrlId = board?.slug || board?.id || boardIdOrSlug;

  // Team mappings for live game boards (used for BoardExport)
  const [teamMappings, setTeamMappings] = useState<TeamMapping[]>([]);
  useEffect(() => {
    if (!board?.externalGameId) return;
    let mounted = true;
    sportsApi.getGame(board.externalGameId).then(data => {
      if (mounted) setTeamMappings(data.teamMappings || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [board?.externalGameId]);

  // Pending score updates for live game boards (managers only)
  const isLiveGame = !!board?.externalGameId;
  const isManager = board?.managers?.some(m => m.userId === user?.id) ?? false;
  const [pendingScoreUpdates, setPendingScoreUpdates] = useState<PendingScoreUpdate[]>([]);
  const fetchPendingScores = useCallback(() => {
    if (!boardIdOrSlug || !isLiveGame || !isManager) return;
    liveScoresApi.getPendingScores(boardIdOrSlug).then(setPendingScoreUpdates).catch(() => {});
  }, [boardIdOrSlug, isLiveGame, isManager]);
  useEffect(() => {
    fetchPendingScores();
  }, [fetchPendingScores]);

  // Tab names for URL path (empty string = board tab at root)
  // Rules is now at position 1 (2nd tab)
  const tabNames = ['board', 'rules', 'players', 'requests', 'audit', 'settings'];
  const currentTab = tabParam || 'board';
  const tabIndex = tabNames.indexOf(currentTab);
  const [tabValue, setTabValue] = useState(tabIndex >= 0 ? tabIndex : 0);

  // Update tabValue when URL changes
  useEffect(() => {
    const newIndex = tabNames.indexOf(currentTab);
    if (newIndex >= 0 && newIndex !== tabValue) {
      setTabValue(newIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync on URL change, not tabValue
  }, [currentTab]);

  // Sync tab with URL using path
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const newTabName = tabNames[newValue] || 'board';
    if (newTabName === 'board') {
      navigate(`/boards/${boardUrlId}`);
    } else {
      navigate(`/boards/${boardUrlId}/${newTabName}`);
    }
  };
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [inviteUrlCopied, setInviteUrlCopied] = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [showWinShare, setShowWinShare] = useState(false);
  const { share, modalOpen: shareModalOpen, shareOptions, closeModal: closeShareModal, trackShare } = useShare();
  const [requestSubmittedMessage, setRequestSubmittedMessage] = useState<string | null>(null);
  const [myRequests, setMyRequests] = useState<SquareRequest[]>([]);
  const [requestLoading, setRequestLoading] = useState(true);
  // Get offline players from global store
  const offlinePlayers = usePlayersStore(selectOfflinePlayers);
  const fetchOfflinePlayersFromStore = usePlayersStore((state) => state.fetchOfflinePlayers);
  const [highlightedData, setHighlightedData] = useState<HighlightedCellsWithQuarter | null>(null);
  const [gameLogTerseMode, setGameLogTerseMode] = useState(true);
  const [boardDeletedDialogOpen, setBoardDeletedDialogOpen] = useState(false);
  const [howToPlayOpen, setHowToPlayOpen] = useState(() => {
    // Show modal by default on first visit to any board
    return !hasSeenEducationModal('how_to_play');
  });

  // Confirmation dialog states
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  const [lockUnclaimedCount, setLockUnclaimedCount] = useState(0);
  const [randomizeConfirmOpen, setRandomizeConfirmOpen] = useState(false);
  const [randomizeCount, setRandomizeCount] = useState(1);
  const [randomizeAnimating, setRandomizeAnimating] = useState(false);
  const [randomizeIteration, setRandomizeIteration] = useState(0);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [addConnectionDialogOpen, setAddConnectionDialogOpen] = useState(false);
  const [createOfflinePlayerDialogOpen, setCreateOfflinePlayerDialogOpen] = useState(false);
  // Create Offline Player form state (standalone, independent of LedgerPanel)
  const [newOfflinePlayerName, setNewOfflinePlayerName] = useState('');
  const [newOfflinePlayerHandle, setNewOfflinePlayerHandle] = useState('');
  const [newOfflinePlayerIcon, setNewOfflinePlayerIcon] = useState('');
  const [newOfflinePlayerApprovedSquares, setNewOfflinePlayerApprovedSquares] = useState(0);
  const [newOfflinePlayerStartingBalance, setNewOfflinePlayerStartingBalance] = useState(0);
  const [newOfflinePlayerError, setNewOfflinePlayerError] = useState<string | null>(null);
  const [creatingOfflinePlayer, setCreatingOfflinePlayer] = useState(false);

  // Assign mode state
  const [assignModeEnabled, setAssignModeEnabled] = useState(false);
  const [assignModePlayer, setAssignModePlayer] = useState<AssignModePlayerSelection | null>(null);
  const [approvedRequests, setApprovedRequests] = useState<SquareRequest[]>([]);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [requestsRefreshTrigger, setRequestsRefreshTrigger] = useState(0);

  // Pending merge requests for current user (to approve/deny)
  const [pendingMergeRequests, setPendingMergeRequests] = useState<MergeRequest[]>([]);
  const [mergeRequestActionLoading, setMergeRequestActionLoading] = useState<string | null>(null);
  // All pending merge requests on the board (for managers to see status)
  const [allPendingMergeRequests, setAllPendingMergeRequests] = useState<MergeRequest[]>([]);
  // Global merge request details (for showing all affected boards in approval UI)
  const [globalMergeDetails, setGlobalMergeDetails] = useState<Record<string, GlobalMergeRequestDetails>>({});
  // Deny dialog state
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [denyingRequestId, setDenyingRequestId] = useState<string | null>(null);
  const [denyingGlobalRequestId, setDenyingGlobalRequestId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');

  // Fetch offline players for the board (using global store)
  const fetchOfflinePlayers = useCallback(async () => {
    if (!boardIdOrSlug) return;
    await fetchOfflinePlayersFromStore(boardIdOrSlug);
  }, [boardIdOrSlug, fetchOfflinePlayersFromStore]);

  // Fetch approved square requests (for knowing user limits in assign mode)
  const fetchApprovedRequests = useCallback(async () => {
    if (!boardIdOrSlug) return;
    try {
      const requests = await squareRequestsApi.list(boardIdOrSlug, 'approved');
      setApprovedRequests(requests);
    } catch (err) {
      console.error('Failed to fetch approved requests:', err);
    }
  }, [boardIdOrSlug]);

  // Fetch pending requests count (for tab badge)
  const fetchPendingRequestsCount = useCallback(async () => {
    if (!boardIdOrSlug) return;
    try {
      const requests = await squareRequestsApi.list(boardIdOrSlug, 'pending');
      setPendingRequestsCount(requests.length);
    } catch (err) {
      console.error('Failed to fetch pending requests count:', err);
    }
  }, [boardIdOrSlug]);

  // Fetch user's square request status
  const fetchMyRequests = useCallback(async () => {
    if (!boardIdOrSlug || !user) return;
    setRequestLoading(true);
    try {
      const requests = await squareRequestsApi.getMyRequests(boardIdOrSlug);
      setMyRequests(requests);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    } finally {
      setRequestLoading(false);
    }
  }, [boardIdOrSlug, user]);

  // Fetch pending merge requests for current user
  const fetchPendingMergeRequests = useCallback(async () => {
    if (!boardIdOrSlug || !user) return;
    try {
      const requests = await mergeRequestsApi.getMyPending(boardIdOrSlug);
      setPendingMergeRequests(requests);

      // Fetch global merge details for any global requests
      const globalIds = requests
        .filter(r => r.globalRequestId)
        .map(r => r.globalRequestId as string);
      const uniqueGlobalIds = [...new Set(globalIds)];

      const detailsMap: Record<string, GlobalMergeRequestDetails> = {};
      await Promise.all(
        uniqueGlobalIds.map(async (globalId) => {
          try {
            const details = await mergeRequestsApi.getGlobalDetails(globalId);
            detailsMap[globalId] = details;
          } catch (err) {
            console.error(`Failed to fetch global merge details for ${globalId}:`, err);
          }
        })
      );
      setGlobalMergeDetails(detailsMap);
    } catch (err) {
      console.error('Failed to fetch pending merge requests:', err);
    }
  }, [boardIdOrSlug, user]);

  // Fetch all pending merge requests on the board (for managers)
  const fetchAllPendingMergeRequests = useCallback(async () => {
    if (!boardIdOrSlug) return;
    try {
      const requests = await mergeRequestsApi.list(boardIdOrSlug, 'pending');
      setAllPendingMergeRequests(requests);
    } catch (err) {
      console.error('Failed to fetch all pending merge requests:', err);
    }
  }, [boardIdOrSlug]);

  // Handle approving a merge request (single or global)
  // Note: globalRequestId is set on child requests that are part of a global merge
  const handleApproveMergeRequest = async (request: MergeRequest) => {
    if (!boardIdOrSlug) return;
    setMergeRequestActionLoading(request.id);
    try {
      if (request.globalRequestId) {
        // This is part of a global merge - use the global approve endpoint
        await mergeRequestsApi.approveGlobal(request.globalRequestId);
      } else {
        // Single board request
        await mergeRequestsApi.approve(boardIdOrSlug, request.id);
      }
      fetchPendingMergeRequests();
      fetchLedger();
      refetchSquares();
      fetchOfflinePlayers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve merge request');
    } finally {
      setMergeRequestActionLoading(null);
    }
  };

  // Open deny dialog for a merge request
  const openDenyDialog = (request: MergeRequest) => {
    setDenyingRequestId(request.id);
    setDenyingGlobalRequestId(request.globalRequestId || null);
    setDenyReason('');
    setDenyDialogOpen(true);
  };

  // Handle denying a merge request (single or global)
  const handleDenyMergeRequest = async () => {
    if (!boardIdOrSlug || !denyingRequestId) return;
    setMergeRequestActionLoading(denyingRequestId);
    try {
      if (denyingGlobalRequestId) {
        // Global request - use the global deny endpoint
        await mergeRequestsApi.denyGlobal(denyingGlobalRequestId, denyReason ? { reason: denyReason } : undefined);
      } else {
        // Single board request
        await mergeRequestsApi.deny(boardIdOrSlug, denyingRequestId, denyReason ? { reason: denyReason } : undefined);
      }
      fetchPendingMergeRequests();
      setDenyDialogOpen(false);
      setDenyingRequestId(null);
      setDenyingGlobalRequestId(null);
      setDenyReason('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deny merge request');
    } finally {
      setMergeRequestActionLoading(null);
    }
  };

  // Handle cancelling a merge request (manager who created it)
  const handleCancelMergeRequest = async (requestId: string) => {
    if (!boardIdOrSlug) return;
    try {
      await mergeRequestsApi.cancel(boardIdOrSlug, requestId);
      fetchAllPendingMergeRequests();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel merge request');
    }
  };

  // Create Offline Player handlers (standalone, for use from assign mode buttons)
  const handleCloseCreateOfflinePlayerDialog = () => {
    setCreateOfflinePlayerDialogOpen(false);
    setNewOfflinePlayerName('');
    setNewOfflinePlayerHandle('');
    setNewOfflinePlayerIcon('');
    setNewOfflinePlayerApprovedSquares(0);
    setNewOfflinePlayerStartingBalance(0);
    setNewOfflinePlayerError(null);
  };

  const handleCreateOfflinePlayer = async () => {
    if (!newOfflinePlayerName.trim()) {
      setNewOfflinePlayerError('Display name is required');
      return;
    }
    if (!boardIdOrSlug) return;

    setCreatingOfflinePlayer(true);
    setNewOfflinePlayerError(null);
    try {
      // Create the offline player and add to board in one step
      await offlinePlayersApi.create(boardIdOrSlug, {
        displayName: newOfflinePlayerName.trim(),
        handle: newOfflinePlayerHandle.trim() || undefined,
        iconName: newOfflinePlayerIcon || undefined,
        approvedSquares: newOfflinePlayerApprovedSquares,
        startingBalance: newOfflinePlayerStartingBalance > 0 ? newOfflinePlayerStartingBalance : undefined,
      });

      // Refresh data
      fetchOfflinePlayers();
      fetchLedger();

      // Close dialog
      handleCloseCreateOfflinePlayerDialog();
    } catch (err) {
      setNewOfflinePlayerError(err instanceof Error ? err.message : 'Failed to create offline player');
    } finally {
      setCreatingOfflinePlayer(false);
    }
  };

  useEffect(() => {
    fetchOfflinePlayers();
    fetchApprovedRequests();
    fetchMyRequests();
    fetchPendingRequestsCount();
    fetchPendingMergeRequests();
    fetchAllPendingMergeRequests();
  }, [fetchOfflinePlayers, fetchApprovedRequests, fetchMyRequests, fetchPendingRequestsCount, fetchPendingMergeRequests, fetchAllPendingMergeRequests]);

  // Debounced callbacks for SSE event handlers to batch rapid successive events
  // This prevents multiple API calls when several events arrive in quick succession
  // Grouped by logical purpose to reduce complexity

  // Core data refreshes
  const debouncedRefreshBoard = useDebouncedCallback(refetch, 300);
  const debouncedRefreshSquares = useDebouncedCallback(refetchSquares, 300);
  const debouncedRefreshHits = useDebouncedCallback(refetchHits, 300);

  // Player and financial data (ledger, offline players, approved requests)
  const debouncedRefreshPlayerData = useDebouncedCallback(() => {
    fetchLedger();
    fetchApprovedRequests();
    fetchOfflinePlayers();
  }, 300);

  // User's request state (my requests, pending count)
  const debouncedRefreshRequestData = useDebouncedCallback(() => {
    fetchMyRequests();
    fetchPendingRequestsCount();
  }, 300);

  // Merge request data
  const debouncedRefreshMergeData = useDebouncedCallback(() => {
    fetchPendingMergeRequests();
    fetchAllPendingMergeRequests();
  }, 300);

  // Subscribe to real-time board events
  useBoardEvents(boardIdOrSlug, {
    onSquareChange: () => {
      debouncedRefreshSquares();
      debouncedRefreshPlayerData();
    },
    onHitChange: (isWinner) => {
      debouncedRefreshHits();
      debouncedRefreshPlayerData();
      // Show confetti and play horn if current user won!
      if (isWinner) {
        // Play celebratory horn/fanfare sound using Web Audio API
        try {
          const audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
          const playTone = (freq: number, startTime: number, duration: number, gain: number) => {
            const osc = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();
            osc.connect(gainNode);
            gainNode.connect(audioCtx.destination);
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(freq, audioCtx.currentTime + startTime);
            gainNode.gain.setValueAtTime(gain, audioCtx.currentTime + startTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + startTime + duration);
            osc.start(audioCtx.currentTime + startTime);
            osc.stop(audioCtx.currentTime + startTime + duration);
          };
          // Simple celebratory fanfare (C-E-G-C chord arpeggio)
          playTone(523.25, 0, 0.15, 0.3);    // C5
          playTone(659.25, 0.1, 0.15, 0.3);  // E5
          playTone(783.99, 0.2, 0.15, 0.3);  // G5
          playTone(1046.5, 0.3, 0.4, 0.4);   // C6 (longer, louder)
          // Close the audio context after sounds finish playing (last tone ends at 0.7s)
          setTimeout(() => {
            audioCtx.close().catch(() => { /* Ignore close errors */ });
          }, 1000);
        } catch {
          // Ignore audio errors
        }

        // First confetti burst
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
          colors: ['#FFD700', '#FFA500', '#32CD32', '#1E90FF', '#FF69B4'],
        });

        // Second confetti burst after a short delay
        setTimeout(() => {
          confetti({
            particleCount: 100,
            spread: 100,
            origin: { y: 0.5, x: 0.3 },
            colors: ['#FFD700', '#FFA500', '#32CD32', '#1E90FF', '#FF69B4'],
          });
          confetti({
            particleCount: 100,
            spread: 100,
            origin: { y: 0.5, x: 0.7 },
            colors: ['#FFD700', '#FFA500', '#32CD32', '#1E90FF', '#FF69B4'],
          });
        }, 300);

        // Show share prompt after celebration
        setTimeout(() => {
          setShowWinShare(true);
        }, 2000);
      }
    },
    onBoardChange: () => {
      debouncedRefreshBoard();
      debouncedRefreshPlayerData(); // Refresh ledger to show bad numbers payouts after board lock
    },
    onRequestStatusChange: () => {
      debouncedRefreshRequestData();
      debouncedRefreshPlayerData(); // Also refresh approved requests for managers' Player tab
      setRequestsRefreshTrigger(prev => prev + 1); // Trigger refresh in SquareRequestsPanel
      debouncedRefreshBoard(); // Refresh board data to get updated participants list for assign mode
    },
    onBoardDeleted: () => {
      setBoardDeletedDialogOpen(true);
    },
    onOfflinePlayerMerged: () => {
      debouncedRefreshPlayerData();
      debouncedRefreshSquares();
    },
    onMergeRequestChange: () => {
      debouncedRefreshMergeData();
    },
    onPendingScoreChange: () => {
      fetchPendingScores();
    },
    onGameStatusChange: () => {
      debouncedRefreshBoard();
    },
    currentUserId: user?.id,
  });

  // Subscribe to global merge request changes (user-level SSE events)
  // This handles cases where the user isn't on the specific board when a global merge is created/cancelled
  useEffect(() => {
    const unsubscribe = subscribeToGlobalMergeChanges(() => {
      console.log('[BoardDetailPage] Global merge request change, refreshing pending requests');
      fetchPendingMergeRequests();
      fetchAllPendingMergeRequests();
    });
    return unsubscribe;
  }, [subscribeToGlobalMergeChanges, fetchPendingMergeRequests, fetchAllPendingMergeRequests]);

  // Handler for clearing merge suggestion query params after dialog opens
  const handleMergeSuggestionConsumed = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleSubmitRequest = async (requestedSquares: number) => {
    if (!boardIdOrSlug) return;
    try {
      setActionError(null);
      const response = await squareRequestsApi.create(boardIdOrSlug, { requestedSquares });
      // Refresh all requests to get the new one
      await fetchMyRequests();
      // Show success message with remaining requests info
      if (response.remainingActiveRequests > 0) {
        setRequestSubmittedMessage(
          `Request submitted! You can submit ${response.remainingActiveRequests} more request${response.remainingActiveRequests !== 1 ? 's' : ''}.`
        );
      } else {
        setRequestSubmittedMessage(
          'Request submitted! You\'ve reached the maximum of 5 active requests. You can submit more after some are processed.'
        );
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to submit request');
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    if (!boardIdOrSlug) return;
    try {
      setActionError(null);
      await squareRequestsApi.delete(boardIdOrSlug, requestId);
      // Refresh all requests
      await fetchMyRequests();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel request');
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading board..." />;
  }

  if (error || !board) {
    // Check if it's a "not found" error
    const isNotFound = error?.toLowerCase().includes('not found') || !board;
    // Check if user must join the board
    const mustJoin = error?.toLowerCase().includes('must join');
    if (isNotFound || mustJoin) {
      return (
        <NotFoundPage
          title={mustJoin ? 'Access Denied' : 'Board Not Found'}
          message={mustJoin
            ? 'You must join this board to view it. Ask the board owner for an invite link.'
            : "This board doesn't exist or you don't have access to it. It may have been deleted or the link is incorrect."}
        />
      );
    }
    return (
      <Alert severity="error">
        {error || 'Something went wrong'}
      </Alert>
    );
  }

  const isOwner = board.createdById === user?.id;
  const isManagerOrOwner = isOwner || (board.managers?.some((m) => m.userId === user?.id) ?? false);

  // Elevated system role permissions for board access
  const isSuperAdminUser = isSuperAdmin(user);
  const isSupportUser = isSupport(user);
  // Can see all tabs (Requests, Settings, etc.)
  const hasManagerView = isManagerOrOwner || isSuperAdminUser || isSupportUser;
  // Can perform manager actions (publish, lock, record scores, edit settings, manage squares)
  const hasManagerControl = isManagerOrOwner || isSuperAdminUser;

  const handleCopyInviteCode = async () => {
    const success = await copyToClipboard(board.inviteCode);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLock = async () => {
    // Wait for squares to load before calculating unclaimed count
    if (isSquaresLoading) {
      await doLockBoard();
      return;
    }

    // Calculate unclaimed cells (convert squares to cells for non-standard modes)
    const boardTotalCells = board?.totalCells || getTotalCells(board?.cellMode || 'standard');
    const boardSquaresPerCell = 100 / boardTotalCells; // 1 for standard, 2 for paired, 4 for quad
    const claimedSquaresCount = grid.flat().filter(s => s?.ownerId || s?.offlinePlayerId).length;
    const claimedCellsCount = Math.round(claimedSquaresCount / boardSquaresPerCell);
    const unclaimedCount = boardTotalCells - claimedCellsCount;

    // Show confirmation if there are unclaimed cells
    if (unclaimedCount > 0) {
      setLockUnclaimedCount(unclaimedCount);
      setLockConfirmOpen(true);
      return;
    }

    await doLockBoard();
  };

  const doLockBoard = async () => {
    try {
      setActionError(null);
      await lockBoard();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to lock board');
    }
  };

  const handleUnlock = async () => {
    try {
      setActionError(null);
      await unlockBoard();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to unlock board');
    }
  };

  const handleRandomize = async () => {
    // Always show the randomize modal for shuffle count selection and swap teams warning
    setRandomizeConfirmOpen(true);
  };

  const doRandomize = async () => {
    try {
      setActionError(null);
      await randomizeNumbers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to randomize numbers');
    }
  };

  const handlePublish = async () => {
    // Show confirmation dialog warning about permanently locked settings
    setPublishConfirmOpen(true);
  };

  const doPublish = async () => {
    try {
      setActionError(null);
      await publishBoard();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to publish board');
    }
  };

  const handleUnpublish = async () => {
    try {
      setActionError(null);
      await unpublishBoard();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to return board to draft');
    }
  };

  const handleRecordPayment = async (userId: string | undefined, offlinePlayerId: string | undefined, amount: number, description: string) => {
    try {
      setActionError(null);
      await ledgerApi.recordPayment(boardIdOrSlug!, { userId, offlinePlayerId, amount, description });
      fetchLedger();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to record payment');
      throw err;
    }
  };

  const handleMarkWinningsPaid = async (userId: string | undefined, offlinePlayerId: string | undefined, paid: boolean) => {
    try {
      setActionError(null);
      await ledgerApi.markWinningsPaid(boardIdOrSlug!, { userId, offlinePlayerId, paid });
      fetchLedger();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update winnings status');
      throw err;
    }
  };

  const handleComplete = async () => {
    setCompleteConfirmOpen(true);
  };

  const doComplete = async () => {
    try {
      setActionError(null);
      await completeBoard();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to complete board');
    }
  };

  const handleResetBoard = async () => {
    setResetConfirmOpen(true);
  };

  const doResetBoard = async () => {
    try {
      setActionError(null);
      await squaresApi.reset(boardIdOrSlug!);
      refetchSquares();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reset board');
    }
  };

  const handleAutofillOffline = async () => {
    try {
      setActionError(null);
      await squaresApi.autofillSquares(boardIdOrSlug!, false); // Only offline players, not manager
      refetchSquares();
      fetchOfflinePlayers(); // Refresh claimed counts
      fetchLedger(); // Update balances
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to autofill squares');
    }
  };

  const handleSwapTeamPositions = async () => {
    try {
      setActionError(null);
      // Default to true if undefined, then toggle
      const currentValue = board.homeOnTop ?? true;
      await updateBoard({ homeOnTop: !currentValue });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to swap team positions');
    }
  };

  // Check if there are any recorded hits - if so, block randomize/reset
  const hasRecordedHits = hits && hits.length > 0;

  // Check if board can be reset: not already reset, no claimed squares, and in draft status
  const hasClaimedSquares = grid.flat().some(square => square?.ownerId || square?.offlinePlayerId);
  const claimedSquaresCount = grid.flat().filter(square => square?.ownerId || square?.offlinePlayerId).length;

  // Calculate cell counts for display (cells = squares / squaresPerCell)
  const squaresPerCell = board.cellMode === 'quad' ? 4 : board.cellMode === 'paired' ? 2 : 1;
  const totalCells = board.totalCells || 100;
  const claimedCellsCount = Math.floor(claimedSquaresCount / squaresPerCell);
  // Don't calculate unclaimed count until squares have loaded (prevents false positives)
  const unclaimedCellsCount = isSquaresLoading ? 0 : totalCells - claimedCellsCount;
  const unitName = 'Squares'; // Always use "Squares" terminology regardless of cell mode

  const canResetBoard = !board.hasBeenReset && !hasClaimedSquares && board.status === 'draft';

  // Calculate if all scores have been recorded
  const allScoresRecorded = (() => {
    if (!board || !hits) return false;
    const otEnabled = board.payoutConfig?.overtimeConfig?.enabled ?? false;
    const quarters = board.quarters || 4;
    // Expected quarters: for quarters=4 with OT, we expect Q1,Q2,Q3,Q4 plus Final(0)
    // For quarters=4 without OT, just Q1,Q2,Q3,Q4
    // For quarters=2 (soccer), just H1,H2
    // For quarters=3 (hockey), P1,P2,P3
    let expectedQuarters: number[];
    if (quarters === 2) {
      expectedQuarters = [1, 2];
    } else if (quarters === 3) {
      expectedQuarters = [1, 2, 3];
    } else {
      expectedQuarters = otEnabled ? [1, 2, 3, 4, 0] : [1, 2, 3, 4];
    }
    return expectedQuarters.every(q => hits.some(h => h.quarter === q));
  })();

  // Calculate how many squares the current user has claimed
  const userClaimedCount = grid.flat().filter(square => square?.ownerId === user?.id).length;

  // Get total approved squares from all user's approved requests (for non-managers)
  const approvedSquaresFromRequests = myRequests.filter(r => r.status === 'approved');
  const approvedSquares = approvedSquaresFromRequests.length === 0
    ? undefined
    : approvedSquaresFromRequests.reduce((sum, r) => sum + (r.approvedSquares || 0), 0);

  const handleInvite = async () => {
    try {
      setInviteError(null);
      await inviteParticipant(inviteEmail);
      setInviteDialogOpen(false);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invite');
    }
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail('');
    setInviteError(null);
  };

  const handleSquareClick = async (row: number, col: number) => {
    try {
      setActionError(null);
      // If in assign mode with a selected player, use assignSquare instead
      if (assignModeEnabled && assignModePlayer) {
        const selectedPlayer = assignModePlayer;

        // Calculate current claimed squares directly from grid, then convert to cells
        const currentClaimedSquares = grid.flat().filter(s =>
          selectedPlayer.isOfflinePlayer
            ? s?.offlinePlayerId === selectedPlayer.id
            : s?.ownerId === selectedPlayer.id
        ).length;
        const currentClaimedCells = Math.round(currentClaimedSquares / squaresPerCell);

        // Check if the player has reached their approved cell limit (stored as cells in database)
        let approvedCells: number | undefined;

        if (selectedPlayer.isOfflinePlayer) {
          // Offline player - get limit from offlinePlayers list (approvedSquares is stored as cells)
          const boardPlayer = (offlinePlayers || []).find(p => p.id === selectedPlayer.id) as BoardOfflinePlayer | undefined;
          approvedCells = boardPlayer?.approvedSquares;
        } else {
          // Check if this is a manager (managers have no limit)
          const isManager = board.managers?.some(m => m.userId === selectedPlayer.id);
          if (!isManager) {
            // Regular participant - check their approved request limit (sum of all approved requests, stored as cells)
            const userRequests = approvedRequests.filter(r => r.userId === selectedPlayer.id);
            approvedCells = userRequests.length > 0
              ? userRequests.reduce((sum, r) => sum + (r.approvedSquares || 0), 0)
              : undefined;
          }
        }

        const cellLabel = 'squares'; // Always use "squares" terminology
        if (approvedCells !== undefined && approvedCells > 0 && currentClaimedCells >= approvedCells) {
          setActionError(`${selectedPlayer.displayName} has already claimed all ${approvedCells} approved ${cellLabel}`);
          return;
        }

        await assignSquare({
          row,
          col,
          userId: selectedPlayer.isOfflinePlayer ? undefined : selectedPlayer.id,
          offlinePlayerId: selectedPlayer.isOfflinePlayer ? selectedPlayer.id : undefined,
        });
        // Refresh data to update counts and balances
        fetchOfflinePlayers();
        fetchLedger();
      } else {
        await claimSquare({ row, col });
        fetchLedger();
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to claim square');
    }
  };

  // Get all players (participants + managers combined and deduped)
  const getAllPlayers = () => {
    const playersMap = new Map<string, { user?: User; role: 'owner' | 'manager' | 'player'; joinedAt?: string; approvedSquares?: number; claimedSquares?: number }>();

    // Helper to count claimed squares for a user
    const getClaimedCount = (userId: string) => grid.flat().filter(s => s?.ownerId === userId).length;

    // Helper to get total approved squares from all requests for a user
    const getApprovedSquares = (userId: string) => {
      const userRequests = approvedRequests.filter(r => r.userId === userId);
      if (userRequests.length === 0) return undefined;
      return userRequests.reduce((sum, r) => sum + (r.approvedSquares || 0), 0);
    };

    // Add owner first - look in managers list for owner role
    const ownerManager = board.managers?.find((m) => m.role === 'owner');
    if (ownerManager) {
      playersMap.set(ownerManager.userId, {
        user: ownerManager.user,
        role: 'owner',
        claimedSquares: getClaimedCount(ownerManager.userId),
        approvedSquares: undefined, // Managers don't have limits
      });
    } else {
      // Fallback: If owner not in managers, add from createdById
      // Try to find their user info from participants
      const ownerParticipant = board.participants?.find((p) => p.userId === board.createdById);
      playersMap.set(board.createdById, {
        user: ownerParticipant?.user,
        role: 'owner',
        claimedSquares: getClaimedCount(board.createdById),
        approvedSquares: undefined,
      });
    }

    // Add other managers
    board.managers?.forEach((m) => {
      if (m.role !== 'owner' && !playersMap.has(m.userId)) {
        playersMap.set(m.userId, {
          user: m.user,
          role: 'manager',
          claimedSquares: getClaimedCount(m.userId),
          approvedSquares: undefined, // Managers don't have limits
        });
      }
    });

    // Add participants (players without manager role)
    board.participants?.forEach((p) => {
      if (!playersMap.has(p.userId)) {
        playersMap.set(p.userId, {
          user: p.user,
          role: 'player',
          joinedAt: p.joinedAt,
          claimedSquares: getClaimedCount(p.userId),
          approvedSquares: getApprovedSquares(p.userId),
        });
      }
    });

    return Array.from(playersMap.entries()).map(([userId, data]) => ({
      userId,
      ...data,
    }));
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' }, gap: { xs: 2, md: 0 }, mb: 2 }}>
          {/* Left side - title, date, owner, description */}
          <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                {board.name}
              </Typography>
              <Tooltip title="How to play">
                <IconButton
                  size="small"
                  onClick={() => setHowToPlayOpen(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy board link">
                <IconButton
                  size="small"
                  onClick={async () => {
                    const shareUrl = `${window.location.origin}/b/${board.slug || board.id}`;
                    const success = await copyToClipboard(shareUrl);
                    if (success) setShareLinkCopied(true);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share board">
                <IconButton
                  size="small"
                  onClick={() => {
                    const shareUrl = `${window.location.origin}/b/${board.slug || board.id}`;
                    share({
                      title: board.name,
                      text: `Check out this squares pool: ${board.awayTeam} vs ${board.homeTeam} on SquareUp!`,
                      url: shareUrl,
                      boardId: board.id,
                      shareContext: 'board',
                    });
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography color="text.secondary">
              {new Date(board.gameDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
            {/* Board Owner */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Hosted by
              </Typography>
              <PlayerAvatar
                displayName={board.owner?.displayName || 'Unknown'}
                handle={board.owner?.handle}
                iconName={board.owner?.iconName}
                size="small"
                showBadge={false}
              />
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                {board.owner?.handle ? `@${board.owner.handle.toUpperCase()}` : board.owner?.displayName || 'Unknown'}
              </Typography>
            </Box>
          </Box>
          {/* Right side - status, invite code, pot totals (stacks vertically on mobile, aligned left) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: { xs: 'flex-start', md: 'flex-end' }, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip label={`$${board.pricePerSquare}/square`} variant="outlined" />
              {/* Status chip */}
              <Chip
                label={board.status === 'draft' ? 'Draft' : board.status === 'locked' ? 'Locked' : board.status === 'completed' ? 'Completed' : 'Live'}
                color={board.status === 'draft' ? 'default' : board.status === 'locked' ? 'error' : board.status === 'completed' ? 'info' : 'success'}
                variant={board.status === 'draft' ? 'outlined' : 'filled'}
                data-testid="board-status"
              />
            </Box>
            {/* Invite Code - show to managers/owners and elevated roles, hide for completed boards */}
            {hasManagerView && board.status !== 'completed' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Invite Code:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', letterSpacing: 2, fontWeight: 'bold' }} data-testid="invite-code">
                  {board.inviteCode}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                  <IconButton size="small" onClick={handleCopyInviteCode}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<InviteIcon />}
                  onClick={() => setInviteDialogOpen(true)}
                >
                  Invite Player
                </Button>
              </Box>
            )}
            {/* Pot totals and cells/squares info */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" data-testid="header-squares-count">
                {unitName}: <Box component="span" sx={{ fontWeight: 'bold', color: claimedCellsCount === totalCells ? 'success.main' : 'text.primary' }}>{claimedCellsCount}/{totalCells} claimed</Box>
                {unclaimedCellsCount > 0 && (
                  <Box component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>({unclaimedCellsCount} available)</Box>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Pot: <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>${summary?.totalPot?.toFixed(2) || '0.00'}</Box>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paid Out: <Box component="span" sx={{ fontWeight: 'bold', color: 'success.main' }}>${summary?.totalPaidOut?.toFixed(2) || '0.00'}</Box>
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
            {/* Description */}
            {board.description && (
              <Box
                dir="ltr"
                sx={{
                  mt: 1,
                  '& a': { color: 'primary.main', textDecoration: 'underline' },
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(board.description) }}
              />
            )}
        </Box>

        {/* Live Game Banner */}
        {board.externalGameId && (
          <Box sx={{ mt: 2 }}>
            <LiveGameBanner externalGameId={board.externalGameId} sport={board.sport} />
          </Box>
        )}

        {/* Pending Merge Requests for current user */}
        {pendingMergeRequests.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.200',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <MergeIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Pending Merge Requests
              </Typography>
              <Chip
                label={pendingMergeRequests.length}
                size="small"
                color="primary"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A manager has requested to merge an offline player&apos;s squares into your account.
              You must approve this before any squares or payment records are transferred.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Link component={RouterLink} to="/support/board-management#merge-offline-players" target="_blank">
                Learn more about merge requests
              </Link>
            </Typography>
            {pendingMergeRequests.map((request) => {
              const globalDetails = request.globalRequestId ? globalMergeDetails[request.globalRequestId] : null;
              return (
                <Paper
                  key={request.id}
                  variant="outlined"
                  sx={{ p: 2, mb: 1, bgcolor: 'background.paper' }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        {request.globalRequestId ? (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip label="Global Merge" size="small" color="secondary" />
                            </Box>
                            <Typography variant="body1">
                              Merge <strong>{request.offlinePlayer?.displayName || 'Unknown Player'}</strong>&apos;s squares across multiple boards into your account
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              This offline player was created by the board manager to track squares on your behalf before you had an account.
                              Approving will transfer ownership of these squares to you.
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="body1">
                              Merge <strong>{request.offlinePlayer?.displayName || 'Unknown Player'}</strong>&apos;s {request.squaresToTransfer} square{request.squaresToTransfer !== 1 ? 's' : ''} into your account
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              This offline player was created by the board manager to track squares on your behalf.
                              Approving will transfer ownership of these squares to you.
                            </Typography>
                          </>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Requested by {request.requestedBy?.displayName || 'Manager'} &bull; Expires {new Date(request.expiresAt).toLocaleDateString()}
                        </Typography>
                        {request.removeOfflinePlayer && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            The offline player will be removed after merge
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'column' },
                        gap: 1,
                        flexShrink: 0,
                        minWidth: { sm: 140 },
                      }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={mergeRequestActionLoading === request.id ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
                          onClick={() => handleApproveMergeRequest(request)}
                          disabled={mergeRequestActionLoading !== null}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {request.globalRequestId ? 'Approve All' : 'Approve'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DenyIcon />}
                          onClick={() => openDenyDialog(request)}
                          disabled={mergeRequestActionLoading !== null}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {request.globalRequestId ? 'Deny All' : 'Deny'}
                        </Button>
                      </Box>
                    </Box>

                    {/* Show global merge board details */}
                    {request.globalRequestId && globalDetails && (
                      <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Boards Included ({globalDetails.boardDetails.length})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Approving will merge squares on all of these boards at once:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {globalDetails.boardDetails.map((boardDetail) => (
                            <Typography component="li" variant="body2" key={boardDetail.boardId}>
                              <strong>{boardDetail.boardName}</strong>: {boardDetail.squareCount} square{boardDetail.squareCount !== 1 ? 's' : ''}
                            </Typography>
                          ))}
                        </Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                          Total: {globalDetails.totalSquares} square{globalDetails.totalSquares !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Paper>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {actionError}
          </Alert>
        )}
      </Box>

      {/* Tabs - scrollable on mobile */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Board" />
          <Tab label="Rules" icon={<RulesIcon />} iconPosition="start" />
          <Tab label="Players" />
          {hasManagerView && (
            <Tab
              label={
                <Badge badgeContent={pendingRequestsCount} color="error" max={99}>
                  Requests
                </Badge>
              }
            />
          )}
          <Tab label="Audit Log" icon={<AuditIcon />} iconPosition="start" />
          {hasManagerView && <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />}
        </Tabs>
      </Box>

      {/* Board Tab - Grid with Hits & Payouts side by side */}
      <TabPanel value={tabValue} index={0} data-testid="tabpanel-board">
        {/* Players tab prompt for owners/managers - only on Board tab */}
        {hasManagerControl && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Manage player payments and winnings in the{' '}
              <Link
                component={RouterLink}
                to={`/boards/${boardUrlId}/players`}
                sx={{ fontWeight: 'bold' }}
              >
                Players
              </Link>{' '}
              tab.
            </Typography>
          </Alert>
        )}

        {/* Square Request Banner for regular users (hidden when board is locked since no squares can be claimed) */}
        {!hasManagerView && !requestLoading && board.status !== 'locked' && (
          <SquareRequestBanner
            requests={myRequests}
            board={board}
            onSubmitRequest={handleSubmitRequest}
            onCancelRequest={handleCancelRequest}
            userClaimedCount={userClaimedCount}
          />
        )}

        {/* Player Stats Summary for non-managers */}
        {!hasManagerView && summary && user && approvedSquares !== undefined && (
          <PlayerStatsBanner
            summary={summary}
            userId={user.id}
            board={board}
            approvedSquares={approvedSquares}
            userClaimedCount={userClaimedCount}
          />
        )}

        {/* My Squares Panel - shows user's squares organized by quarter */}
        {user && (
          <MySquaresPanel
            board={board}
            grid={grid}
            hits={hits}
            currentUserId={user.id}
            summary={summary}
            onHighlight={setHighlightedData}
          />
        )}

        {/* Manager Controls - inside Board tab (hidden on completed or locked boards) */}
        {hasManagerControl && board.status !== 'completed' && board.status !== 'locked' && (
          <Box sx={{
            mb: 3,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, auto)' },
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
          }}>
            {/* Swap team positions - disabled after numbers are randomized */}
            <Tooltip title={board.numbersRevealed ? 'Cannot swap teams after numbers are randomized' : ''}>
              <span style={{ display: 'flex' }}>
                <Button
                  variant="outlined"
                  startIcon={<SwapIcon />}
                  onClick={handleSwapTeamPositions}
                  disabled={board.numbersRevealed}
                  size="small"
                  fullWidth
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Swap Teams
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={hasRecordedHits ? 'Cannot randomize after scores are recorded' : ''}>
              <span style={{ display: 'flex' }}>
                <Button
                  variant="outlined"
                  startIcon={<ShuffleIcon />}
                  onClick={handleRandomize}
                  disabled={hasRecordedHits}
                  size="small"
                  fullWidth
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid="randomize-button"
                >
                  Randomize
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={hasRecordedHits ? 'Cannot autofill after scores are recorded' : 'Fill empty squares with offline players based on their approved squares'}>
              <span style={{ display: 'flex' }}>
                <Button
                  variant="outlined"
                  startIcon={<AutofillIcon />}
                  onClick={handleAutofillOffline}
                  disabled={hasRecordedHits}
                  size="small"
                  fullWidth
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Autofill
                </Button>
              </span>
            </Tooltip>
            {/* Reset button only shown for draft boards that haven't been reset and have no claimed squares */}
            {canResetBoard && (
              <Tooltip title={hasRecordedHits ? 'Cannot reset after scores are recorded' : ''}>
                <span style={{ display: 'flex' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ResetIcon />}
                    onClick={handleResetBoard}
                    disabled={hasRecordedHits}
                    size="small"
                    fullWidth
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Reset
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}

        <ThemeWrapper themeData={board.theme?.themeData} showAnimations={true}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 2, lg: 4 }, alignItems: 'flex-start', width: '100%' }}>
          {/* Squares Grid */}
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', lg: 'auto' }, overflow: 'auto' }}>
              <SquaresGrid
                board={board}
                grid={grid}
                hits={hits}
                offlinePlayers={offlinePlayers}
                onSquareClick={handleSquareClick}
                onAssignSquare={async (row, col, userId, offlinePlayerId) => {
                  try {
                    setActionError(null);
                    await assignSquare({ row, col, userId, offlinePlayerId });
                    fetchLedger(); // Update balances
                  } catch (err) {
                    setActionError(err instanceof Error ? err.message : 'Failed to assign square');
                  }
                }}
                onClearSquare={async (row, col) => {
                  try {
                    setActionError(null);
                    await clearSquare(row, col);
                    refetchSquares();
                    fetchLedger(); // Update balances
                  } catch (err) {
                    setActionError(err instanceof Error ? err.message : 'Failed to clear square');
                  }
                }}
                currentUserId={user?.id}
                isManager={hasManagerControl}
                approvedSquares={hasManagerControl ? undefined : approvedSquares}
                userClaimedCount={userClaimedCount}
                highlightedCells={highlightedData?.cells}
                activeQuarter={highlightedData?.quarter}
                assignModeEnabled={assignModeEnabled && !!assignModePlayer}
                assignModePlayerId={assignModePlayer?.id}
                assignModeAtLimit={(() => {
                  if (!assignModeEnabled || !assignModePlayer) return false;
                  // Calculate current claimed squares for the player, convert to cells
                  const playerSquares = grid.flat().filter(s =>
                    assignModePlayer.isOfflinePlayer
                      ? s?.offlinePlayerId === assignModePlayer.id
                      : s?.ownerId === assignModePlayer.id
                  ).length;
                  const playerCells = Math.round(playerSquares / squaresPerCell);

                  let approvedCells: number | undefined;

                  if (assignModePlayer.isOfflinePlayer) {
                    // Offline player - get limit from offlinePlayers list (approvedSquares is stored as cells)
                    const boardPlayer = (offlinePlayers || []).find(p => p.id === assignModePlayer.id) as BoardOfflinePlayer | undefined;
                    approvedCells = boardPlayer?.approvedSquares;
                  } else {
                    // Check if this is a manager (managers have no limit)
                    const isManager = board.managers?.some(m => m.userId === assignModePlayer.id);
                    if (isManager) return false;

                    // Regular participant - check their approved request limit (sum of all approved requests, stored as cells)
                    const userRequests = approvedRequests.filter(r => r.userId === assignModePlayer.id);
                    approvedCells = userRequests.length > 0
                      ? userRequests.reduce((sum, r) => sum + (r.approvedSquares || 0), 0)
                      : undefined;
                  }

                  return approvedCells !== undefined && approvedCells > 0 && playerCells >= approvedCells;
                })()}
                hideNames={board.hideNames && (board.status === 'draft' || board.status === 'live')}
              />
          </Box>

          {/* Game Log Panel */}
          <Box sx={{
            flex: '1 1 300px',
            minWidth: { xs: 0, sm: 280 },
            width: { xs: '100%', lg: 'auto' },
            // Add top padding to align with the grid content (accounts for team header)
            pt: { xs: 0, lg: 4 },
            // Add semi-transparent background when theme is applied for text visibility
            ...(board.theme && {
              backgroundColor: board.theme.themeData?.textColorMode === 'light'
                ? 'rgba(0,0,0,0.5)'
                : 'rgba(255,255,255,0.9)',
              borderRadius: 2,
              p: 2,
              color: board.theme.themeData?.textColorMode === 'light' ? '#fff' : undefined,
            }),
          }}>
            {/* Assign Mode UI - for managers in draft or live mode */}
            {hasManagerControl && (board.status === 'draft' || board.status === 'live') && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: assignModeEnabled ? 'primary.50' : 'grey.50', border: assignModeEnabled ? 2 : 0, borderColor: 'primary.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: assignModeEnabled ? 2 : 0 }}>
                  <AssignIcon color={assignModeEnabled ? 'primary' : 'disabled'} />
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    Assign Mode
                  </Typography>
                  <Switch
                    checked={assignModeEnabled}
                    onChange={(e) => {
                      setAssignModeEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setAssignModePlayer(null);
                      }
                    }}
                    size="small"
                  />
                </Box>
                {assignModeEnabled && (
                  <>
                    <AssignModeDropdown
                      boardId={boardIdOrSlug!}
                      selectedPlayer={assignModePlayer}
                      onPlayerSelect={setAssignModePlayer}
                      squaresPerCell={squaresPerCell}
                    />
                    {/* Action buttons below dropdown for easy access */}
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOfflinePlayerDialogOpen(true)}
                      >
                        Create Offline Player
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setAddConnectionDialogOpen(true)}
                      >
                        Add Player
                      </Button>
                    </Box>
                    {assignModePlayer && (() => {
                      // Calculate claimed squares for the selected player, then convert to cells
                      const playerSquares = grid.flat().filter(s =>
                        assignModePlayer.isOfflinePlayer
                          ? s?.offlinePlayerId === assignModePlayer.id
                          : s?.ownerId === assignModePlayer.id
                      ).length;
                      const playerCells = Math.round(playerSquares / squaresPerCell);

                      // Get approved cells limit (stored as cells in the database)
                      let approvedCells: number | undefined;
                      if (assignModePlayer.isOfflinePlayer) {
                        // Offline player - get limit from offlinePlayers list (approvedSquares is stored as cells)
                        const boardPlayer = (offlinePlayers || []).find(p => p.id === assignModePlayer.id) as BoardOfflinePlayer | undefined;
                        approvedCells = boardPlayer?.approvedSquares;
                      } else {
                        // Check if this is a manager (managers have no limit)
                        const isManager = board.managers?.some(m => m.userId === assignModePlayer.id);
                        if (!isManager) {
                          // Regular participant - get total limit from all approved requests (stored as cells)
                          const userRequests = approvedRequests.filter(r => r.userId === assignModePlayer.id);
                          approvedCells = userRequests.length > 0
                            ? userRequests.reduce((sum, r) => sum + (r.approvedSquares || 0), 0)
                            : undefined;
                        }
                      }

                      const hasLimit = approvedCells !== undefined && approvedCells > 0;
                      const isAtLimit = hasLimit && approvedCells !== undefined && playerCells >= approvedCells;

                      return (
                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="caption" color={isAtLimit ? 'error.main' : 'text.secondary'}>
                            {isAtLimit ? 'Player at limit - click cells to unassign' : 'Click empty cells to assign'}
                          </Typography>
                          <Chip
                            size="small"
                            icon={<AssignIcon sx={{ fontSize: 14 }} />}
                            label={hasLimit ? `${playerCells}/${approvedCells}` : `${playerCells} assigned`}
                            color={isAtLimit ? 'error' : 'primary'}
                            variant="filled"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                      );
                    })()}
                  </>
                )}
              </Paper>
            )}

            {/* Anonymous Mode Toggle - for managers on draft or live boards */}
            {hasManagerControl && (board.status === 'draft' || board.status === 'live') && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: board.hideNames ? 'warning.50' : 'grey.50', border: board.hideNames ? 2 : 0, borderColor: 'warning.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityOffIcon color={board.hideNames ? 'warning' : 'disabled'} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">
                      Anonymous Until Locked
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Hide player names until board is locked
                    </Typography>
                  </Box>
                  <Switch
                    checked={board.hideNames}
                    onChange={async (e) => {
                      await updateBoard({ hideNames: e.target.checked });
                    }}
                    size="small"
                  />
                </Box>
              </Paper>
            )}

            {/* Board Progress Guidance for Managers - above Game Log */}
            {hasManagerControl && board.status !== 'completed' && (
              <BoardProgressGuidance
                board={board}
                hasRecordedHits={hasRecordedHits}
                claimedSquaresCount={grid.flat().filter(s => s?.ownerId || s?.offlinePlayerId).length}
                allScoresRecorded={allScoresRecorded}
              />
            )}

            {/* Status action buttons and download */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fit, minmax(100px, auto))' },
              gap: 1,
              mb: 2,
              width: { xs: '100%', sm: 'auto' },
            }}>
              {/* Manager status buttons */}
              {hasManagerControl && board.status !== 'completed' && (
                <>
                  {board.status === 'draft' && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      startIcon={<PublishIcon />}
                      onClick={handlePublish}
                      data-testid="publish-button"
                    >
                      Publish
                    </Button>
                  )}
                  {board.status === 'live' && (
                    <>
                      <Tooltip title={hasRecordedHits ? 'Cannot return to draft after scores are recorded' : 'Return to draft mode'}>
                        <span style={{ display: 'flex' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="inherit"
                            startIcon={<UnpublishIcon />}
                            onClick={handleUnpublish}
                            disabled={hasRecordedHits}
                            fullWidth
                            data-testid="unpublish-button"
                          >
                            Unpublish
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<LockIcon />}
                        onClick={handleLock}
                        data-testid="lock-button"
                      >
                        Lock
                      </Button>
                    </>
                  )}
                  {board.status === 'locked' && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<LockOpenIcon />}
                      onClick={handleUnlock}
                      data-testid="unlock-button"
                    >
                      Unlock
                    </Button>
                  )}
                </>
              )}
              {/* Download - available to everyone */}
              <BoardExport board={board} grid={grid} hits={hits} buttonText="Download" hideNames={board.hideNames && (board.status === 'draft' || board.status === 'live')} teamMappings={teamMappings} />
            </Box>

            {/* Congratulatory message for completed boards */}
            {board.status === 'completed' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Game Complete!
                </Typography>
                <Typography variant="body2">
                  Final scores are locked. Head to the Players tab to settle up with players.
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ color: 'inherit' }}>
                Game Log
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
                  Winners
                </Typography>
                <Switch
                  size="small"
                  checked={!gameLogTerseMode}
                  onChange={(e) => setGameLogTerseMode(!e.target.checked)}
                  data-testid="winners-toggle"
                />
              </Box>
            </Box>
            <HitsPanel board={board} hits={hits} isManager={hasManagerControl} boardId={boardIdOrSlug!} currentUserId={user?.id} onRecordHit={() => { refetchHits(); fetchLedger(); fetchPendingScores(); }} onHitHover={setHighlightedData} onComplete={handleComplete} showCustomPayouts={true} terseMode={gameLogTerseMode} textColorMode={board.theme?.themeData?.textColorMode} unclaimedSquaresCount={unclaimedCellsCount} pendingScoreUpdates={pendingScoreUpdates} isLiveGame={isLiveGame} />

            {/* Bad Numbers Winners - shown when Winners toggle is ON */}
            {!gameLogTerseMode && <BadNumbersWinnersSection ledgerEntries={ledgerEntries || []} />}

            {/* Link to public Winners page when there are recorded hits */}
            {hits.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  component={RouterLink}
                  to={`/b/${board.slug || board.id}/winners`}
                  sx={{ textTransform: 'none' }}
                >
                  View Winners Page
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        </ThemeWrapper>
      </TabPanel>

      {/* Rules Tab - now at position 1 */}
      <TabPanel value={tabValue} index={1} data-testid="tabpanel-rules">
        <RulesPanel board={board} />
      </TabPanel>

      {/* Players Tab - includes player management */}
      <TabPanel value={tabValue} index={2} data-testid="tabpanel-players">
        <LedgerPanel
          summary={summary}
          ledgerEntries={ledgerEntries}
          board={board}
          hits={hits}
          isManager={hasManagerControl}
          isOwner={isOwner}
          currentUserId={user?.id}
          onRecordPayment={handleRecordPayment}
          onMarkWinningsPaid={handleMarkWinningsPaid}
          players={getAllPlayers()}
          offlinePlayers={offlinePlayers}
          participants={board.participants || []}
          onPromoteToManager={async (userId: string) => {
            try {
              await boardsApi.addManager(boardIdOrSlug!, userId);
              refetch();
            } catch (err) {
              setActionError(err instanceof Error ? err.message : 'Failed to promote to manager');
            }
          }}
          onRemoveManager={async (userId: string) => {
            try {
              await boardsApi.removeManager(boardIdOrSlug!, userId);
              refetch();
            } catch (err) {
              setActionError(err instanceof Error ? err.message : 'Failed to remove manager');
            }
          }}
          onOfflinePlayersChange={() => {
            fetchOfflinePlayers();
            refetchSquares(); // Refresh squares in case assignments changed
            fetchLedger(); // Refresh balances to show new offline players
          }}
          onPlayerRemoved={(playerId) => {
            // Clear assign mode selection if the removed player was selected
            if (assignModePlayer?.id === playerId && assignModePlayer?.isOfflinePlayer) {
              setAssignModePlayer(null);
            }
          }}
          pendingMergeRequests={allPendingMergeRequests}
          onCancelMergeRequest={handleCancelMergeRequest}
          onMergeRequestCreated={fetchAllPendingMergeRequests}
          onOpenAddConnectionDialog={() => setAddConnectionDialogOpen(true)}
          onRemoveParticipant={async (userId: string) => {
            try {
              await boardsApi.removeParticipant(boardIdOrSlug!, userId);
              // Clear assign mode selection if the removed player was selected
              if (assignModePlayer?.id === userId && !assignModePlayer?.isOfflinePlayer) {
                setAssignModePlayer(null);
              }
              refetch();
              refetchSquares();
              fetchLedger();
            } catch (err) {
              setActionError(err instanceof Error ? err.message : 'Failed to remove player');
            }
          }}
          mergeSuggestionOfflinePlayerId={mergeSuggestionOfflinePlayerId || undefined}
          mergeSuggestionTargetUserId={mergeSuggestionTargetUserId || undefined}
          onMergeSuggestionConsumed={handleMergeSuggestionConsumed}
        />
      </TabPanel>

      {/* Requests Tab (managers and elevated roles) */}
      {hasManagerView && (
        <TabPanel value={tabValue} index={3} data-testid="tabpanel-requests">
          <SquareRequestsPanel boardId={boardIdOrSlug!} isManager={hasManagerControl} currentUserId={user?.id} onRequestStatusChange={() => { fetchApprovedRequests(); fetchPendingRequestsCount(); refetch(); }} refreshTrigger={requestsRefreshTrigger} />
        </TabPanel>
      )}

      {/* Audit Tab - visible to all */}
      <TabPanel value={tabValue} index={hasManagerView ? 4 : 3} data-testid="tabpanel-audit">
        <AuditPanel boardId={boardIdOrSlug!} />
      </TabPanel>

      {/* Settings Tab (managers and elevated roles) */}
      {hasManagerView && (
        <TabPanel value={tabValue} index={5} data-testid="tabpanel-settings">
          <SettingsPanel
            board={board}
            readOnly={isSupportUser}
            onSavePayoutConfig={async (payoutConfig) => {
              try {
                setActionError(null);
                await boardsApi.update(boardIdOrSlug!, { payoutConfig });
                refetch();
              } catch (err) {
                setActionError(err instanceof Error ? err.message : 'Failed to update payout settings');
              }
            }}
            onSaveBasicInfo={async (info) => {
              try {
                setActionError(null);
                await boardsApi.update(boardIdOrSlug!, info);
                refetch();
              } catch (err) {
                setActionError(err instanceof Error ? err.message : 'Failed to update board settings');
              }
            }}
            onDuplicate={async (input) => {
              const newBoard = await boardsApi.duplicate(boardIdOrSlug!, input);
              // Refresh credits after successful duplication
              await refreshCredits();
              navigate(`/boards/${newBoard.slug || newBoard.id}`);
            }}
            onDelete={async () => {
              await boardsApi.delete(boardIdOrSlug!);
              navigate('/boards');
            }}
            onBoardUpdated={refetch}
          />
        </TabPanel>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleCloseInviteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Player</DialogTitle>
        <DialogContent>
          {inviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}

          {/* Invite URL Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Invite Link (for players)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Share this link with people you want to join your board. Clicking this link will automatically add them as a player so they can claim squares.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
              }}
            >
              {window.location.origin}/boards/{board.slug || board.id}/invite/{board.inviteCode}
            </Typography>
            <Tooltip title={inviteUrlCopied ? 'Copied!' : 'Copy link'}>
              <IconButton
                size="small"
                onClick={async () => {
                  const success = await copyToClipboard(`${window.location.origin}/boards/${board.slug || board.id}/invite/${board.inviteCode}`);
                  if (success) {
                    setInviteUrlCopied(true);
                    setTimeout(() => setInviteUrlCopied(false), 2000);
                  }
                }}
                sx={{ flexShrink: 0 }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Email Invite Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Or Send Email Invite
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Enter an email address to send a direct invitation. The recipient will receive an email with a link to join this board.
          </Typography>
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            error={!!inviteError}
            sx={{ mb: 3 }}
          />

          {/* Public Board Link Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Public Board Link (view-only)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Share this link for people who just want to watch the game. They can see the board, scores, and winners but cannot join or claim squares.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
              }}
            >
              {window.location.origin}/b/{board.slug || board.id}
            </Typography>
            <Tooltip title={publicLinkCopied ? 'Copied!' : 'Copy link'}>
              <IconButton
                size="small"
                onClick={async () => {
                  const success = await copyToClipboard(`${window.location.origin}/b/${board.slug || board.id}`);
                  if (success) {
                    setPublicLinkCopied(true);
                    setTimeout(() => setPublicLinkCopied(false), 2000);
                  }
                }}
                sx={{ flexShrink: 0 }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog}>Cancel</Button>
          <Button onClick={handleInvite} variant="contained">
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Board Deleted Dialog */}
      <Dialog
        open={boardDeletedDialogOpen}
        disableEscapeKeyDown
        onClose={() => {}}
      >
        <DialogTitle>Board Deleted</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This board has been deleted by its owner.
          </Alert>
          <Typography variant="body1">
            The board you were viewing is no longer available. Click OK to return to your boards list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => navigate('/boards')}
            variant="contained"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deny Merge Request Dialog */}
      <Dialog
        open={denyDialogOpen}
        onClose={() => !mergeRequestActionLoading && setDenyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DenyIcon color="error" />
            <Typography variant="h6" component="span">Deny Merge Request</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are about to deny this merge request. The offline player&apos;s squares will not be transferred to your account.
          </Typography>
          <TextField
            fullWidth
            label="Reason for denial (optional)"
            placeholder="e.g., I don't recognize this offline player"
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            multiline
            rows={2}
            disabled={!!mergeRequestActionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDenyDialogOpen(false)}
            disabled={!!mergeRequestActionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDenyMergeRequest}
            variant="contained"
            color="error"
            disabled={!!mergeRequestActionLoading}
            startIcon={mergeRequestActionLoading ? <CircularProgress size={16} color="inherit" /> : <DenyIcon />}
          >
            {mergeRequestActionLoading ? 'Denying...' : 'Deny Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock Confirmation Dialog */}
      <ConfirmDialog
        open={lockConfirmOpen}
        title={`Lock board with unclaimed ${unitName.toLowerCase()}?`}
        icon="warning"
        message={`There are ${lockUnclaimedCount} ${unitName.toLowerCase()} that have not been claimed.

Once locked, no additional ${unitName.toLowerCase()} can be claimed.

Are you sure you want to lock the board?`}
        confirmLabel="Lock Board"
        confirmColor="warning"
        onConfirm={() => {
          setLockConfirmOpen(false);
          doLockBoard();
        }}
        onCancel={() => setLockConfirmOpen(false)}
      />

      {/* Randomize Confirmation Dialog */}
      <Dialog
        open={randomizeConfirmOpen}
        onClose={() => !randomizeAnimating && setRandomizeConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShuffleIcon color="info" />
            <Typography variant="h6" component="span">Randomize Numbers</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {randomizeAnimating ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                Shuffle {randomizeIteration} of {randomizeCount}
              </Typography>
              <CircularProgress size={60} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Randomizing numbers...
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Once revealed, participants will see the number assignments.
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                You can re-randomize until scores are recorded.
              </Typography>

              {/* Warning about swap teams */}
              {!board.numbersRevealed && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> After randomizing, you will no longer be able to swap team positions. Make sure the teams are in the correct order before proceeding.
                  </Typography>
                </Alert>
              )}

              {/* Randomization count selector */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Number of shuffles: {randomizeCount}
                </Typography>
                <Slider
                  value={randomizeCount}
                  onChange={(_, value) => setRandomizeCount(value as number)}
                  min={1}
                  max={10}
                  step={1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                  ]}
                  valueLabelDisplay="auto"
                />
                <Typography variant="caption" color="text.secondary">
                  Watch the numbers shuffle multiple times for added excitement!
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRandomizeConfirmOpen(false);
              setRandomizeCount(1);
            }}
            disabled={randomizeAnimating}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setRandomizeAnimating(true);
              setRandomizeIteration(0);

              // Actually randomize the numbers the specified number of times
              // with a delay between calls to avoid rate limiting
              for (let i = 1; i <= randomizeCount; i++) {
                setRandomizeIteration(i);
                try {
                  await doRandomize();
                } catch (_err) {
                  // If an error occurs, stop and show error
                  setRandomizeAnimating(false);
                  setRandomizeConfirmOpen(false);
                  setRandomizeCount(1);
                  return;
                }
                // Delay between API calls to avoid rate limiting (except on the last one)
                if (i < randomizeCount) {
                  await new Promise(resolve => setTimeout(resolve, 800));
                }
              }

              setRandomizeAnimating(false);
              setRandomizeConfirmOpen(false);
              setRandomizeCount(1);
            }}
            color="primary"
            variant="contained"
            disabled={randomizeAnimating}
          >
            {randomizeAnimating ? 'Shuffling...' : 'Randomize'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <ConfirmDialog
        open={publishConfirmOpen}
        title="Publish this board?"
        icon="warning"
        confirmLabel="Publish Board"
        confirmColor="primary"
        onConfirm={() => {
          setPublishConfirmOpen(false);
          doPublish();
        }}
        onCancel={() => setPublishConfirmOpen(false)}
      >
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Once published, the following settings will be <strong>permanently locked</strong>:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, mb: 2 }}>
            <li><Typography variant="body2">Board name and URL slug</Typography></li>
            <li><Typography variant="body2">Sport, teams, and game date</Typography></li>
            <li><Typography variant="body2">Price per square</Typography></li>
            <li><Typography variant="body2">Payout configuration</Typography></li>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You can still update:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, mb: 2 }}>
            <li><Typography variant="body2">Description</Typography></li>
            <li><Typography variant="body2">Team colors</Typography></li>
            <li><Typography variant="body2">Display orientation</Typography></li>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This cannot be undone, even if you unpublish the board.
          </Alert>
        </Box>
      </ConfirmDialog>

      {/* Complete Board Confirmation Dialog */}
      <ConfirmDialog
        open={completeConfirmOpen}
        title="Complete this board?"
        icon="warning"
        confirmLabel="Complete Board"
        confirmColor="success"
        onConfirm={() => {
          setCompleteConfirmOpen(false);
          doComplete();
        }}
        onCancel={() => setCompleteConfirmOpen(false)}
      >
        <Box>
          <Box component="ul" sx={{ m: 0, pl: 2, mb: 2 }}>
            <li><Typography variant="body2">All scores will be locked and cannot be edited</Typography></li>
            <li><Typography variant="body2">Square assignments cannot be changed</Typography></li>
            <li><Typography variant="body2">You can still record payments and mark winnings as paid</Typography></li>
          </Box>
          <Alert severity="warning">
            This action cannot be undone.
          </Alert>
        </Box>
      </ConfirmDialog>

      {/* Reset Board Confirmation Dialog */}
      <ConfirmDialog
        open={resetConfirmOpen}
        title="Reset Board"
        icon="error"
        message="Are you sure you want to reset the board? This will clear all square assignments."
        confirmLabel="Reset Board"
        confirmColor="error"
        onConfirm={() => {
          setResetConfirmOpen(false);
          doResetBoard();
        }}
        onCancel={() => setResetConfirmOpen(false)}
      />

      {/* Share link copied snackbar */}
      <Snackbar
        open={shareLinkCopied}
        autoHideDuration={2000}
        onClose={() => setShareLinkCopied(false)}
        message="Share link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Request submitted snackbar */}
      <Snackbar
        open={!!requestSubmittedMessage}
        autoHideDuration={5000}
        onClose={() => setRequestSubmittedMessage(null)}
        message={requestSubmittedMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Win share prompt */}
      <Snackbar
        open={showWinShare}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="You won! Share your victory?"
        action={
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const shareUrl = `${window.location.origin}/b/${board.slug || board.id}/winners`;
                share({
                  title: `I won on ${board.name}!`,
                  text: `I just won on ${board.name} on SquareUp!`,
                  url: shareUrl,
                  boardId: board.id,
                  shareContext: 'win',
                });
                setShowWinShare(false);
              }}
            >
              Share
            </Button>
            <IconButton size="small" color="inherit" onClick={() => setShowWinShare(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      {/* Share Modal (fallback when native share is unavailable) */}
      {shareOptions && (
        <ShareModal
          open={shareModalOpen}
          onClose={closeShareModal}
          title={shareOptions.title}
          text={shareOptions.text}
          url={shareOptions.url}
          boardId={shareOptions.boardId}
          shareContext={shareOptions.shareContext}
          onTrackShare={trackShare}
        />
      )}

      {/* How to Play Modal */}
      <HowToPlayModal
        open={howToPlayOpen}
        onClose={() => {
          markEducationModalSeen('how_to_play');
          setHowToPlayOpen(false);
        }}
        board={board}
      />

      {/* Add Connection Dialog */}
      <AddConnectionDialog
        open={addConnectionDialogOpen}
        onClose={() => setAddConnectionDialogOpen(false)}
        boardId={board.id}
        existingPlayerIds={[
          // Only include users who actually have cells assigned on this board
          // This ensures "Added" status only shows for players with cells, not just those with
          // participant records but 0 cells (who wouldn't show in Players table)
          ...(() => {
            const usersWithCells = new Set<string>();
            grid.flat().forEach(s => {
              if (s?.ownerId) {
                usersWithCells.add(s.ownerId);
              }
            });
            return Array.from(usersWithCells);
          })(),
        ]}
        existingOfflinePlayerIds={(() => {
          // Only include offline players who actually have cells assigned
          const offlinePlayersWithCells = new Set<string>();
          grid.flat().forEach(s => {
            if (s?.offlinePlayerId) {
              offlinePlayersWithCells.add(s.offlinePlayerId);
            }
          });
          return Array.from(offlinePlayersWithCells);
        })()}
        onPlayersChanged={() => {
          refetch();
          refetchSquares();
          fetchOfflinePlayers();
          fetchLedger(); // Refresh balances to update players table
        }}
        squaresPerCell={squaresPerCell}
      />

      {/* Create Offline Player Dialog (standalone, for assign mode) */}
      <Dialog open={createOfflinePlayerDialogOpen} onClose={handleCloseCreateOfflinePlayerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Offline Player</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Display Name"
              value={newOfflinePlayerName}
              onChange={(e) => setNewOfflinePlayerName(e.target.value)}
              required
              error={!!newOfflinePlayerError && !newOfflinePlayerName.trim()}
              helperText={!newOfflinePlayerName.trim() && newOfflinePlayerError ? 'Display name is required' : ''}
            />
            <TextField
              fullWidth
              label="Handle (4 characters)"
              value={newOfflinePlayerHandle}
              onChange={(e) => setNewOfflinePlayerHandle(e.target.value.slice(0, 4).toUpperCase())}
              placeholder="e.g., JOHN"
              helperText="4-character code shown on squares"
              inputProps={{ maxLength: 4 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Avatar Icon (optional)
              </Typography>
              <IconPicker value={newOfflinePlayerIcon} onChange={setNewOfflinePlayerIcon} />
            </Box>
            <TextField
              fullWidth
              type="number"
              label="Approved Squares"
              value={newOfflinePlayerApprovedSquares}
              onChange={(e) => setNewOfflinePlayerApprovedSquares(Math.max(0, parseInt(e.target.value) || 0))}
              inputProps={{ min: 0, max: 100 }}
              helperText="Max squares this player can claim (0 = unlimited)"
            />
            <TextField
              fullWidth
              type="number"
              label="Starting Balance"
              value={newOfflinePlayerStartingBalance}
              onChange={(e) => setNewOfflinePlayerStartingBalance(Math.max(0, parseFloat(e.target.value) || 0))}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Pre-paid amount (e.g., cash received upfront)"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            {newOfflinePlayerError && (
              <Alert severity="error">{newOfflinePlayerError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateOfflinePlayerDialog} disabled={creatingOfflinePlayer}>Cancel</Button>
          <Button onClick={handleCreateOfflinePlayer} variant="contained" disabled={creatingOfflinePlayer || !newOfflinePlayerName.trim()}>
            {creatingOfflinePlayer ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
